import _get from 'lodash/get'
import _split from 'lodash/split'
import _last from 'lodash/last'
import _first from 'lodash/first'
import _find from 'lodash/find'
import _trim from 'lodash/trim'
import _filter from 'lodash/filter'
import _toPairs from 'lodash/toPairs'
import _isString from 'lodash/isString'
import _isArray from 'lodash/isArray'
import _isObject from 'lodash/isObject'
import _isEmpty from 'lodash/isEmpty'
import _isNil from 'lodash/isNil'
import { delay as wait, fromCallback as fromCallbackPromise } from 'bluebird'
import { getExponentialDelay } from '../../exponential-backoff/index'
import { get as getConfig } from '../../config/index'
import { isBrowser } from '../../config/environment'
import { getCookie, getAllCookies } from '../cookie/index'
import { getEverFlowTransactionData } from './transaction-cookie'

export {
  EVERFLOW_TRANSACTIONS_COOKIE,
  getEverFlowTransactionData,
  hasEverflowTransactionCookie,
} from './transaction-cookie'

const config = getConfig()
const defaultOfferId = _get(config, ['everflow', 'defaultOfferId'])

export const EVERFLOW_OFFER_ID_DEFAULT = defaultOfferId
export const EVERFLOW_AFFILIATE_ID_COOKIE = 'ef_affid'
export const EVERFLOW_OID_REGEX = /^ef_tid_c_o_\d+$/

// Script loading state management
const SCRIPT_LOADING_STATES = {
  NOT_STARTED: 'not_started',
  LOADING: 'loading',
  LOADED: 'loaded',
  FAILED: 'failed',
  TIMEOUT: 'timeout',
}

let scriptLoadingState = SCRIPT_LOADING_STATES.NOT_STARTED
let loadingPromise = null
let loadingTimeout = null

// Configuration constants
const SCRIPT_URL = 'https://www.gja4trk.com/scripts/sdk/everflow.js'
const DEFAULT_TIMEOUT = 10000 // 10 seconds
const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_RETRY_DELAY = 300
const JITTER_FACTOR = 0.1 // 10% jitter for retry delays
const INITIALIZATION_DELAY = 100 // Small delay to allow SDK initialization
const READINESS_CHECK_ATTEMPTS = 5 // Number of times to check if SDK is ready
const READINESS_CHECK_DELAY = 200 // Delay between readiness checks

/**
 * Add jitter to retry delay to prevent thundering herd
 * @param {number} delay - Base delay in milliseconds
 * @returns {number} Delay with jitter applied
 */
function addJitter (delay) {
  const jitter = delay * JITTER_FACTOR * (Math.random() * 2 - 1) // -10% to +10%
  return Math.max(0, Math.floor(delay + jitter))
}

/**
 * Check if we have network connectivity
 * @returns {boolean} True if online
 */
function isOnline () {
  if (typeof navigator !== 'undefined' && 'onLine' in navigator) {
    return navigator.onLine
  }
  return true // Assume online if we can't determine
}

/**
 * Verify that the EverFlow SDK loaded correctly
 * @returns {boolean} True if SDK is properly loaded
 */
function verifyScriptLoaded () {
  const { EF } = global
  if (!EF) {
    return false
  }

  // Check if EF has expected methods/properties
  if (typeof EF.click !== 'function') {
    return false
  }

  return true
}

/**
 * Wait for EverFlow SDK to be fully initialized and ready
 * @param {number} attempts - Current attempt number
 * @returns {Promise<boolean>} True if SDK is ready
 */
async function waitForSDKReady (attempts = 0) {
  if (attempts >= READINESS_CHECK_ATTEMPTS) {
    return false
  }

  // Check if SDK is loaded and has expected methods
  if (!verifyScriptLoaded()) {
    await wait(READINESS_CHECK_DELAY)
    return waitForSDKReady(attempts + 1)
  }

  // Additional check: ensure the SDK is actually functional
  // Some SDKs need time to initialize internal state even after methods are available
  try {
    // Test if the SDK can handle a simple operation without throwing
    // We'll do a basic check without actually calling the method
    const { EF } = global
    if (EF && typeof EF.click === 'function') {
      // Give a small additional delay for any internal initialization
      await wait(INITIALIZATION_DELAY)
      return true
    }
  } catch (e) {
    // If there's an error, wait and try again
    await wait(READINESS_CHECK_DELAY)
    return waitForSDKReady(attempts + 1)
  }

  return false
}

/**
 * Clean up loading state and timeouts
 */
function cleanupLoadingState () {
  if (loadingTimeout) {
    clearTimeout(loadingTimeout)
    loadingTimeout = null
  }
  scriptLoadingState = SCRIPT_LOADING_STATES.NOT_STARTED
  loadingPromise = null
}

/**
 * Load the EverFlow script with timeout and retry logic
 * @param {number} attempt - Current attempt number
 * @param {number} maxAttempts - Maximum attempts allowed
 * @param {number} timeout - Timeout in milliseconds
 * @returns {Promise<void>}
 */
async function loadScriptWithTimeout (attempt, maxAttempts, timeout) {
  return new Promise((resolve, reject) => {
    // Set up timeout
    loadingTimeout = setTimeout(() => {
      scriptLoadingState = SCRIPT_LOADING_STATES.TIMEOUT
      const timeoutError = new Error(`EverFlow script loading timed out after ${timeout}ms`)
      cleanupLoadingState()
      reject(timeoutError)
    }, timeout)

    // Load script asynchronously
    const loadScript = async () => {
      try {
        const { default: scriptjs } = await import('scriptjs')
        await fromCallbackPromise((cb) => scriptjs(SCRIPT_URL, () => cb()))

        // Clear timeout on success
        if (loadingTimeout) {
          clearTimeout(loadingTimeout)
          loadingTimeout = null
        }

        // Wait for SDK to be fully ready
        const isReady = await waitForSDKReady()
        if (isReady) {
          scriptLoadingState = SCRIPT_LOADING_STATES.LOADED
          resolve()
        } else {
          throw new Error('EverFlow SDK failed to initialize properly')
        }
      } catch (error) {
        // Clear timeout on error
        if (loadingTimeout) {
          clearTimeout(loadingTimeout)
          loadingTimeout = null
        }
        reject(error)
      }
    }

    loadScript()
  })
}

export async function getEverFlowScript (
  attempt = 0,
  maxAttempt = DEFAULT_MAX_ATTEMPTS,
  timeout = DEFAULT_TIMEOUT,
) {
  // Check if we're in browser environment
  if (!isBrowser()) {
    return
  }

  // Check if script is already loaded
  if (global.EF && verifyScriptLoaded()) {
    return
  }

  // Check if we're already loading (prevent concurrent loads)
  if (scriptLoadingState === SCRIPT_LOADING_STATES.LOADING && loadingPromise) {
    await loadingPromise
    return
  }

  // Check if we've already failed and should not retry
  if (scriptLoadingState === SCRIPT_LOADING_STATES.FAILED && attempt === 0) {
    return
  }

  // Check network connectivity
  if (!isOnline()) {
    scriptLoadingState = SCRIPT_LOADING_STATES.FAILED
    return
  }

  // Set loading state
  scriptLoadingState = SCRIPT_LOADING_STATES.LOADING

  try {
    loadingPromise = loadScriptWithTimeout(attempt, maxAttempt, timeout)
    await loadingPromise
    cleanupLoadingState()
  } catch (error) {
    scriptLoadingState = SCRIPT_LOADING_STATES.FAILED

    // Retry logic with exponential backoff and jitter
    if (attempt < maxAttempt) {
      const baseDelay = getExponentialDelay(attempt, DEFAULT_RETRY_DELAY)
      const delayWithJitter = addJitter(baseDelay)

      await wait(delayWithJitter)

      // Reset state for retry
      scriptLoadingState = SCRIPT_LOADING_STATES.NOT_STARTED
      loadingPromise = null

      await getEverFlowScript(attempt + 1, maxAttempt, timeout)
      return
    }

    cleanupLoadingState()
  }
}

export function getEverFlowAffiliateIdData () {
  if (!isBrowser()) {
    return null
  }
  return getCookie(EVERFLOW_AFFILIATE_ID_COOKIE)
}

export function getLatestTransactionId () {
  const tids = getEverFlowTransactionData()
  if (_isNil(tids) || !_isString(tids) || _isEmpty(_trim(tids))) {
    return null
  }
  return _last(_split(tids, '|'))
}

export function getEverFlowOidFromCookie () {
  if (!isBrowser()) {
    return null
  }

  // Validate that getAllCookies returns valid data
  const cookies = getAllCookies()
  if (_isNil(cookies) || (!_isString(cookies) && !_isObject(cookies))) {
    return null
  }

  // Ensure cookies is an object (not a string)
  if (_isString(cookies)) {
    return null
  }

  // Filter cookies that match the EVERFLOW_OID_REGEX pattern
  // Add validation for each key-value pair
  const everflowOidCookies = _filter(_toPairs(cookies), ([key, value]) => {
    return _isString(key)
      && _isString(value)
      && !_isEmpty(key)
      && !_isEmpty(value)
      && EVERFLOW_OID_REGEX.test(key)
  })

  // Early return if no matching cookies found
  if (_isEmpty(everflowOidCookies)) {
    return null
  }

  // Get the latest transaction ID using the existing function
  const lastTid = getLatestTransactionId()
  if (_isNil(lastTid) || !_isString(lastTid) || _isEmpty(_trim(lastTid))) {
    return null
  }

  // Find the cookie that matches the last TID
  const matchingCookie = _find(everflowOidCookies, ([, value]) => {
    return _isString(value) && _trim(value) === _trim(lastTid)
  })

  if (_isNil(matchingCookie) || !_isArray(matchingCookie) || matchingCookie.length < 2) {
    return null
  }

  // Extract the number from the end of the cookie key with validation
  const cookieKey = _first(matchingCookie)
  if (!_isString(cookieKey) || _isEmpty(cookieKey)) {
    return null
  }

  const match = cookieKey.match(/\d+$/)
  if (_isNil(match) || !_isArray(match) || _isEmpty(_first(match))) {
    return null
  }

  return _first(match)
}
